import type { EmbeddedIndustryAnalysis } from "@/lib/types/industry";
import { IndustryFreshnessBanner } from "./IndustryFreshnessBanner";
import { IndustryStitchPanel } from "./IndustryStitchPanel";
import { IndustryStructuralCard } from "./IndustryStructuralCard";
import { IndustryRecentEvents } from "./IndustryRecentEvents";

interface Props {
  data: EmbeddedIndustryAnalysis;
  companyName: string;
  isExpanded: boolean;
  onToggle: () => void;
  sectionRef?: (el: HTMLDivElement | null) => void;
}

export function IndustrySection({ data, companyName, isExpanded, onToggle, sectionRef }: Props) {
  const subtitleBase =
    data.subIndustry || `${titleCase(data.sector)} (${data.market === "india" ? "India" : "US"})`;
  const subtitle =
    data.usedFallback
      ? `${titleCase(data.sector)} (sector-wide view)`
      : titleCase(subtitleBase);

  const headerId = "industry-tailwinds-header";
  const panelId = "industry-tailwinds-panel";

  return (
    <div
      id="industry-tailwinds"
      ref={sectionRef}
      className={`bg-surface border rounded-xl p-5 mb-2 transition scroll-mt-20 ${
        isExpanded ? "border-blue/40" : "border-surface-light/30"
      }`}
    >
      {/* Heading wraps the button (APG disclosure pattern, same as the
          report sections) — gives SR users both the document outline AND
          a keyboard-operable toggle. h2 + button styles are reset so the
          visual rendering is identical to the old div row. */}
      <h2 id={headerId} className="m-0">
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={onToggle}
          className="w-full flex items-center justify-between cursor-pointer bg-transparent border-none p-0 text-left"
        >
          <span className="flex items-center gap-2.5">
            <span className="text-lg" aria-hidden="true">📈</span>
            <span className="text-foreground font-semibold text-[15px]">
              Industry Tailwinds &amp; Headwinds
            </span>
          </span>
          <span
            aria-hidden="true"
            className={`text-dim text-lg transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>
      </h2>
      {/* Animated content area — aria-hidden while collapsed so screen
          readers don't read the visually-hidden body. */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        aria-hidden={!isExpanded}
        className={`section-collapsible ${isExpanded ? "expanded" : ""}`}
      >
        <div className="section-inner">
          <div className="section-content">
            <div className="mt-4 pt-4 border-t border-surface-light/30 space-y-4">
              <div className="flex items-center gap-2">
                <IndustryFreshnessBanner
                  cacheGeneratedAt={data.cacheGeneratedAt}
                  subtitle={subtitle}
                />
                {data.usedFallback && (
                  <span
                    title="Showing sector-wide view. Sub-industry analysis will be available shortly."
                    className="cursor-help text-slate-500"
                  >
                    ⓘ
                  </span>
                )}
              </div>

              {data.stitch && (
                <IndustryStitchPanel companyName={companyName} stitch={data.stitch} />
              )}

              <div className="grid gap-6 lg:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Structural View · 2-5 year outlook
                  </h3>
                  <IndustryStructuralCard
                    structural={data.structural}
                    relevantTailwinds={data.stitch?.relevantTailwinds}
                    relevantHeadwinds={data.stitch?.relevantHeadwinds}
                  />
                </div>
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Recent Developments · Last 60 days
                  </h3>
                  <IndustryRecentEvents recent={data.recent} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function titleCase(s: string) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}
